import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import ConfirmDialog from '../components/ConfirmDialog';
import api from '../services/api';
import { FiUser, FiMail, FiBook, FiEdit, FiSave, FiKey, FiFileText, FiClock, FiCheck, FiX, FiAlertCircle, FiLogOut, FiTrash2 } from 'react-icons/fi';

const Profile = ({ onLogout }) => {
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [colleges, setColleges] = useState([]);
  const [userRequests, setUserRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);

  const userData = localStorage.getItem('user');
  const initialUser = userData ? JSON.parse(userData) : null;

  const [user, setUser] = useState(initialUser);
  const [formData, setFormData] = useState({
    name: initialUser?.name || '',
    email: initialUser?.email || '',
    collegeId: initialUser?.collegeId || ''
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showDeleteAccountConfirm, setShowDeleteAccountConfirm] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        collegeId: user.collegeId || ''
      });
      if (user.role === 'student') {
        fetchUserRequests();
      }
    }
    fetchColleges();
  }, [user]);

  const fetchColleges = async () => {
    try {
      const response = await api.get('/colleges');
      setColleges(response || []);
    } catch (error) {
      console.error('Error fetching colleges:', error);
    }
  };

  const fetchUserRequests = async () => {
    try {
      setLoadingRequests(true);
      const response = await api.get('/requests/my');
      setUserRequests(response || []);
    } catch (error) {
      console.error('Error fetching user requests:', error);
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.put('/auth/profile', formData);
      const updatedUser = { ...user, ...formData };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);

      setEditMode(false);
      alert('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      alert(error.response?.data?.message || 'Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('New passwords do not match');
      return;
    }

    setLoading(true);

    try {
      await api.put('/auth/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      alert('Password changed successfully');
    } catch (error) {
      console.error('Error changing password:', error);
      alert(error.response?.data?.message || 'Failed to change password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getCollegeName = (collegeId) => {
    const college = colleges.find(c => c._id === collegeId);
    return college ? `${college.collegeName} (${college.collegeCode})` : 'Loading...';
  };

  const getStatusBadge = (status) => {
    if (status === 'approved') return <span className="badge badge-student"><FiCheck /> Approved</span>;
    if (status === 'rejected') return <span className="badge badge-admin"><FiX /> Rejected</span>;
    return <span className="badge badge-teacher"><FiClock /> Pending</span>;
  };

  if (!user) {
    return (
      <div className="dashboard-container">
        <Sidebar onLogout={onLogout} />
        <div className="main-content">
          <div className="text-center p-5"><div className="spinner mx-auto"></div><p className="mt-2 text-secondary">Loading profile...</p></div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <Sidebar onLogout={onLogout} />

      <div className="main-content">
        <Header
          title="Account Settings"
          subtitle="Manage your personal information, security, and account preferences."
        />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          {/* Personal Information */}
          <div className="card">
            <div className="d-flex justify-between align-center mb-4">
              <h3 style={{ fontSize: '16px', fontWeight: '600' }}>Personal Information</h3>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setEditMode(!editMode)}
              >
                <FiEdit /> {editMode ? 'Cancel' : 'Edit Profile'}
              </button>
            </div>

            <form onSubmit={handleUpdateProfile}>
              <div className="form-group">
                <label><FiUser /> Full Name</label>
                {editMode ? (
                  <input
                    type="text"
                    className="form-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                ) : (
                  <div className="form-input" style={{ display: 'flex', alignItems: 'center', backgroundColor: 'var(--bg-surface-hover)' }}>
                    {user.name}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label><FiMail /> Email Address</label>
                {editMode ? (
                  <input
                    type="email"
                    className="form-input"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                ) : (
                  <div className="form-input" style={{ display: 'flex', alignItems: 'center', backgroundColor: 'var(--bg-surface-hover)' }}>
                    {user.email}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label><FiBook /> Account Role</label>
                <div style={{ marginTop: '4px' }}>
                  <span className={`badge badge-${user.role}`}>
                    {user.role.toUpperCase()}
                  </span>
                </div>
              </div>

              <div className="form-group">
                <label><FiBook /> Institution / College</label>
                {editMode ? (
                  <select
                    className="form-input"
                    value={formData.collegeId}
                    onChange={(e) => setFormData({ ...formData, collegeId: e.target.value })}
                    required
                  >
                    <option value="">Select College</option>
                    {colleges.map(college => (
                      <option key={college._id} value={college._id}>
                        {college.collegeName} ({college.collegeCode})
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="form-input" style={{ display: 'flex', alignItems: 'center', backgroundColor: 'var(--bg-surface-hover)' }}>
                    {getCollegeName(user.collegeId)}
                  </div>
                )}
              </div>

              {editMode && (
                <button
                  type="submit"
                  className="btn btn-primary w-100 mt-3"
                  disabled={loading}
                >
                  <FiSave /> {loading ? 'Saving...' : 'Save Profile Changes'}
                </button>
              )}
            </form>
          </div>

          {/* Security & Password */}
          <div className="card">
            <h3 className="mb-4" style={{ fontSize: '16px', fontWeight: '600' }}>
              <FiKey style={{ verticalAlign: 'middle', marginRight: '6px' }} /> Security & Password
            </h3>

            <form onSubmit={handleChangePassword}>
              <div className="form-group">
                <label>Current Password</label>
                <input
                  type="password"
                  className="form-input"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  required
                  placeholder="••••••••"
                />
              </div>

              <div className="form-group">
                <label>New Password</label>
                <input
                  type="password"
                  className="form-input"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  required
                  minLength="6"
                  placeholder="Min. 6 characters"
                />
              </div>

              <div className="form-group">
                <label>Confirm New Password</label>
                <input
                  type="password"
                  className="form-input"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  required
                  minLength="6"
                  placeholder="Re-enter new password"
                />
              </div>

              <button
                type="submit"
                className="btn btn-secondary w-100 mt-3"
                disabled={loading}
              >
                {loading ? 'Updating Password...' : 'Update Password'}
              </button>
            </form>
          </div>
        </div>

        {/* Request History (Students Only) */}
        {user?.role === 'student' && (
          <div className="card mt-4">
            <h3 className="mb-3" style={{ fontSize: '16px', fontWeight: '600' }}>
              <FiFileText style={{ verticalAlign: 'middle', marginRight: '6px' }} /> Request Activity History
            </h3>
            {loadingRequests ? (
              <div className="text-center p-4"><div className="spinner mx-auto"></div><p className="mt-2 text-secondary">Loading activity...</p></div>
            ) : userRequests.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-strong)', background: 'var(--bg-surface-hover)' }}>
                      <th style={{ padding: '10px 14px', textAlign: 'left' }}>Title</th>
                      <th style={{ padding: '10px 14px', textAlign: 'left' }}>Subject</th>
                      <th style={{ padding: '10px 14px', textAlign: 'left' }}>Status</th>
                      <th style={{ padding: '10px 14px', textAlign: 'left' }}>Date</th>
                      <th style={{ padding: '10px 14px', textAlign: 'right' }}>Document</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userRequests.map((req) => (
                      <tr key={req._id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                        <td style={{ padding: '10px 14px', fontWeight: '600' }}>{req.title}</td>
                        <td style={{ padding: '10px 14px' }}>{req.subject} (Sem {req.semester})</td>
                        <td style={{ padding: '10px 14px' }}>{getStatusBadge(req.status)}</td>
                        <td style={{ padding: '10px 14px', color: 'var(--text-secondary)' }}>
                          {new Date(req.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </td>
                        <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                          <button
                            onClick={() => window.open(`${process.env.REACT_APP_API_URL}${req.fileUrl}`, '_blank')}
                            className="btn btn-sm btn-ghost"
                          >
                            <FiFileText /> View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state p-4 text-center">
                <p>No note submission requests found.</p>
              </div>
            )}
          </div>
        )}

        {/* Danger Zone */}
        <div className="card mt-4" style={{ borderColor: 'rgba(239, 68, 68, 0.3)' }}>
          <h3 className="mb-2" style={{ fontSize: '16px', fontWeight: '600', color: 'var(--danger)' }}>
            <FiAlertCircle style={{ verticalAlign: 'middle', marginRight: '6px' }} /> Danger Zone
          </h3>
          <p className="text-secondary text-sm mb-4">
            Actions performed here affect your session and active account credentials.
          </p>

          <div className="d-flex gap-3">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowLogoutConfirm(true)}
            >
              <FiLogOut /> Logout
            </button>

            <button
              type="button"
              className="btn btn-danger"
              onClick={() => setShowDeleteAccountConfirm(true)}
            >
              <FiTrash2 /> Delete Account
            </button>
          </div>
        </div>

        {/* Confirm Logout Dialog */}
        <ConfirmDialog
          isOpen={showLogoutConfirm}
          title="Sign Out of Notebook?"
          message="Are you sure you want to log out of your current active session?"
          confirmText="Logout"
          onConfirm={onLogout}
          onCancel={() => setShowLogoutConfirm(false)}
          isDanger={false}
        />

        {/* Confirm Delete Account Dialog */}
        <ConfirmDialog
          isOpen={showDeleteAccountConfirm}
          title="Permanently Delete Account?"
          message="This action will delete your user profile and remove your access. This action cannot be undone."
          confirmText="Delete My Account"
          onConfirm={() => {
            setShowDeleteAccountConfirm(false);
            alert('Account deletion request acknowledged.');
          }}
          onCancel={() => setShowDeleteAccountConfirm(false)}
          isDanger={true}
        />
      </div>
    </div>
  );
};

export default Profile;